import { shallowRef } from 'vue';

const deferredInstallPrompt = shallowRef(null);
let initialized = false;

const SNOOZE_KEY = 'ml_pwa_install_snooze_until';

export const installPromptState = deferredInstallPrompt;

export const initInstallPromptCapture = () => {
  if (initialized || typeof window === 'undefined') return;
  initialized = true;

  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault();
    deferredInstallPrompt.value = event;
  });

  window.addEventListener('appinstalled', () => {
    deferredInstallPrompt.value = null;
    clearSnooze();
  });
};

export const clearInstallPrompt = () => {
  deferredInstallPrompt.value = null;
};

export const isPwaInstalled = () => {
  if (typeof window === 'undefined') return false;
  const mediaStandalone = window.matchMedia?.('(display-mode: standalone)').matches === true;
  const navigatorStandalone = window.navigator?.standalone === true;
  return mediaStandalone || navigatorStandalone;
};

export const getSnoozeUntil = () => {
  if (typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem(SNOOZE_KEY);
    if (!raw) return null;
    const value = Number(raw);
    if (!Number.isFinite(value)) return null;
    return value;
  } catch (e) {
    void e;
    return null;
  }
};

export const setSnooze = (hours = 24) => {
  if (typeof localStorage === 'undefined') return;
  const durationMs = Math.max(1, Number(hours) || 24) * 60 * 60 * 1000;
  try {
    localStorage.setItem(SNOOZE_KEY, String(Date.now() + durationMs));
  } catch (e) {
    void e;
  }
};

export const clearSnooze = () => {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.removeItem(SNOOZE_KEY);
  } catch (e) {
    void e;
  }
};

const isSnoozed = () => {
  const snoozeUntil = getSnoozeUntil();
  return typeof snoozeUntil === 'number' && Date.now() < snoozeUntil;
};

const isPushSubscribed = (pushStatus) => {
  if (typeof pushStatus === 'boolean') return pushStatus;
  if (pushStatus && typeof pushStatus === 'object') {
    return Boolean(pushStatus.isSubscribed ?? pushStatus.subscribed ?? pushStatus.active);
  }
  return false;
};

export const isInstallPromptEligible = ({ beforeInstallPromptAvailable = false, heuristicInstallable = false } = {}) => {
  if (isPwaInstalled()) return false;
  return Boolean(beforeInstallPromptAvailable || heuristicInstallable);
};

export const shouldShowInstallPrompt = ({
  userSettings,
  pushStatus,
  beforeInstallPromptAvailable = false,
  heuristicInstallable = false
} = {}) => {
  if (!isInstallPromptEligible({ beforeInstallPromptAvailable, heuristicInstallable })) return false;
  if (Boolean(userSettings?.pwaInstallPromptOptOut)) return false;
  if (isSnoozed()) return false;
  if (isPushSubscribed(pushStatus)) return false;
  return true;
};

export const detectPushSubscription = async () => {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return false;
  try {
    const registration = await navigator.serviceWorker.getRegistration();
    if (!registration?.pushManager) return false;
    const subscription = await registration.pushManager.getSubscription();
    return Boolean(subscription);
  } catch (e) {
    void e;
    return false;
  }
};
